import React from "react";
import { DollarSign, Receipt, AlertTriangle, Wallet, TrendingUp } from "lucide-react";

const AccountSummary = ({ financialSummary, loadingSummary=true }) => {
  if (!financialSummary) return null;

  // const hasOverdue = financialSummary?.overDueAmount && 
  //   financialSummary?.overDueAmount !== "$0.00";
    if (loadingSummary) {
      return <AccountSummarySkeleton />;
    }

    const parseMoney = (value) => {
    if (!value) return 0;
    // Removes currency symbol, spaces, commas etc; keeps digits, dot, minus
    const cleaned = value.replace(/[^\d.-]/g, "");
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : 0;
  };

const overdueValue = parseMoney(financialSummary?.overDueAmount);
const hasOverdue = overdueValue > 0;

  const allFieldsEmpty = Object.values(financialSummary || {}).every((v) => v === "" || v === null || v === undefined);

  if (allFieldsEmpty) {
    return <AccountSummaryEmpty />;
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 my-4 min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-green-50 rounded-lg flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-green-600" />
          </div>
          <h3 className="font-semibold text-gray-900">Account Summary</h3>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        
        {/* Overdue */}
        {hasOverdue && (
          <div className="bg-red-50 rounded-lg p-4 shadow-md">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <span className="text-xs text-red-600">Overdue</span>
            </div>
            <p className="text-lg font-semibold text-red-700">
              {financialSummary.overDueAmount}
            </p>
            {financialSummary?.lastDueDate && (
              <p className="text-xs text-red-500 mt-1">
                Since {financialSummary.lastDueDate}
              </p>
            )}
          </div>
        )}

        {/* Invoiced */}
        {financialSummary?.totalStudentInvoiced && 
         financialSummary?.totalStudentInvoiced !== "$0.00" && (
          <div className="bg-white rounded-lg p-4 shadow-md border border-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <Receipt className="w-4 h-4 text-blue-400" />
              <span className="text-xs text-blue-500">Invoiced</span>
            </div>
            <p className="text-lg font-semibold text-blue-900">
              {financialSummary.totalStudentInvoiced}
            </p>
          </div>
        )}

        {/* Receipted */}
        {financialSummary?.totalStudentReceipted && 
         financialSummary?.totalStudentReceipted !== "$0.00" && (
          <div className="bg-white rounded-lg p-4 shadow-md border border-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-green-500" />
              <span className="text-xs text-green-600">Receipted</span>
            </div>
            <p className="text-lg font-semibold text-green-700">
              {financialSummary.totalStudentReceipted}
            </p>
          </div>
        )}

        {/* Balance Owing */}
        {financialSummary?.totalBalanceOwing && 
        (
          <div className="bg-white rounded-lg p-4 shadow-md border border-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <Wallet className="w-4 h-4 text-orange-500" />
              <span className="text-xs text-orange-600">Balance Owing</span>
            </div>
            <p className="text-lg font-semibold text-orange-700">
              {financialSummary.totalBalanceOwing}
            </p>
          </div>
        )}

      </div>

      {/* Last Receipt Info
      {financialSummary?.lastReceiptAmount && 
       financialSummary?.lastReceiptAmount !== "$0.00" && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Last Payment</span>
            <div className="text-right">
              <span className="font-medium text-gray-900">
                {financialSummary.lastReceiptAmount}
              </span>
              {financialSummary?.lastReceiptDate && (
                <span className="text-gray-400 ml-2">
                  on {financialSummary.lastReceiptDate}
                </span>
              )}
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default AccountSummary;


const AccountSummarySkeleton = () => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 my-4 min-h-full animate-pulse">
      {/* Header Skeleton */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-gray-200 rounded-lg"></div>
          <div className="h-5 w-40 bg-gray-200 rounded"></div>
        </div>
      </div>

      {/* Stats Grid Skeleton */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, index) => (
          <div key={index} className="bg-gray-50 rounded-lg p-4 shadow-md border border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-4 h-4 bg-gray-200 rounded"></div>
              <div className="h-3 w-16 bg-gray-200 rounded"></div>
            </div>
            <div className="h-6 w-20 bg-gray-200 rounded mb-1"></div>
            <div className="h-3 w-24 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    </div>
  );
};

const AccountSummaryEmpty = () => (
  <div className="bg-white rounded-xl border border-gray-200 p-6 my-4 min-h-full">
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 bg-gray-50 rounded-lg flex items-center justify-center">
        <DollarSign className="w-5 h-5 text-gray-400" />
      </div>
      <div>
        <h3 className="font-semibold text-gray-900">Account Summary</h3>
        <p className="text-sm text-gray-500 mt-1">
          No financial data available.
        </p>
      </div>
    </div>
  </div>
)